import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { HandlingUnit, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface HandlingUnitStore {
  handlingUnits: Ref<Loadable<HandlingUnit[]>>;
  loadHandlingUnits: (opts?: PerformLoadOptions) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useHandlingUnitStore = defineStore(
  'handlingUnit',
  (): HandlingUnitStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const toast = useToast();

    const handlingUnits: Ref<Loadable<HandlingUnit[]>> = ref({
      loading: false,
    });

    const loadHandlingUnits = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const numHandlingUnits = handlingUnits.value.data?.length ?? 0;
      const isLoading = handlingUnits.value.loading;
      const requiresLoad = numHandlingUnits === 0 && !isLoading;

      if (options.bypassCache || requiresLoad) {
        return performLoad();
      }
    };

    const performLoad = async () => {
      try {
        handlingUnits.value.loading = true;
        handlingUnits.value.data = await api.handlingUnit.findAll();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        handlingUnits.value.error = e;
        console.error('Error loading handling units', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('referenceData.handlingUnits', 2),
          }),
          error: e,
        });
      } finally {
        handlingUnits.value.loading = false;
      }
    };

    watch(selectedDomainId, () => {
      handlingUnits.value.data = [];
    });

    return {
      handlingUnits,
      loadHandlingUnits,
    };
  },
);

export const useHandlingUnitStore = (): HandlingUnitStore => {
  const store = _useHandlingUnitStore();
  const { handlingUnits } = storeToRefs(store);
  const { loadHandlingUnits } = store;

  return {
    handlingUnits,
    loadHandlingUnits,
  };
};
